import {
  IUpdateWorkOrderPOPackageProgressDto,
  IUpdateWorkOrderPOProgressDto,
} from "api/models/request/work-order-po/work-order-po-process-update";
import { ObjectSchemaType } from "commons/ultilities/dynamic-form/hooks/use-form";
import { InputType } from "commons/ultilities/dynamic-form/models/control";
import { IForm } from "commons/ultilities/dynamic-form/models/form";
import { date, number, string } from "yup";

export type WorkOrderProcessUnit = 'paper' | 'pcs';

const completedAmountMustNotExceedAmountMessage = (unit: string) =>
  `Số Lượng H/T (${unit}) phải bằng hoặc nhỏ hơn Số Lượng S/X (${unit})`;

const printPaperCompletedAmountMustNotExceedAmountMessage =
  "Số Lượng H/T (tờ) phải bằng hoặc nhỏ hơn Số Lượng S/X (tờ)";

const completedAmountMustMatchPackageAmountMessage =
  "Số Lượng H/T phải bằng Số Kiện Chẵn x SL/ Kiện (pcs) + S/L Kiện Lẻ 1 (pcs) + S/L Kiện Lẻ 2 (pcs)";

const getPackageAmountValidationValues = (form: IForm) => {
  const completedAmount = form.getControl("completedAmount")?.value as
    | number
    | null
    | undefined;
  const packageNumber = form.getControl("packageNumber")?.value as
    | number
    | null
    | undefined;
  const packageAmountPerUnit = form.getControl("packageAmountPerUnit")?.value as
    | number
    | null
    | undefined;
  const packageAmount = form.getControl("packageAmount")?.value as
    | number
    | null
    | undefined;
  const packageAmount2 = form.getControl("packageAmount2")?.value as
    | number
    | null
    | undefined;

  return {
    completedAmount,
    packageNumber,
    packageAmountPerUnit,
    packageAmount,
    packageAmount2,
  };
};

// Mirrors WorkOrderPOProgressDtos.CompletedAmount (backend) so the read-only
// package completedAmount field always reflects the current form state.
export const computeCompletedAmountFromPackage = (
  packageNumber: number | null | undefined,
  packageAmountPerUnit: number | null | undefined,
  packageAmount: number | null | undefined,
  packageAmount2: number | null | undefined,
) => {
  if (
    (packageNumber == null || packageAmountPerUnit == null) &&
    packageAmount == null &&
    packageAmount2 == null
  )
    return null;

  return (
    (packageNumber ?? 0) * (packageAmountPerUnit ?? 0) +
    (packageAmount ?? 0) +
    (packageAmount2 ?? 0)
  );
};

const isValidCompletedAmountByPackage = (form: IForm) => {
  const {
    completedAmount,
    packageNumber,
    packageAmountPerUnit,
    packageAmount,
    packageAmount2,
  } = getPackageAmountValidationValues(form);

  if (completedAmount == null) return true;

  const hasPackageInputs =
    packageNumber != null ||
    packageAmountPerUnit != null ||
    packageAmount != null ||
    packageAmount2 != null;

  if (!hasPackageInputs) return true;

  if (
    (packageNumber == null && packageAmountPerUnit != null) ||
    (packageNumber != null && packageAmountPerUnit == null)
  ) {
    return true;
  }

  const calculatedCompletedAmount =
    (packageNumber ?? 0) * (packageAmountPerUnit ?? 0) +
    (packageAmount ?? 0) +
    (packageAmount2 ?? 0);

  return completedAmount === calculatedCompletedAmount;
};

export const getStandardProcessFormSchema = (
  form: IForm,
  unit: WorkOrderProcessUnit,
): ObjectSchemaType<IUpdateWorkOrderPOProgressDto> => {
  const unitLabel = unit === "paper" ? "tờ" : "pcs";
  const amountLabel = `Số Lượng S/X (${unitLabel})`;
  return {
    date: {
      gridSize: 6,
      label: "Ngày S/X",
      type: InputType.datePicker,
      validattionYupSchema: date().nullable(),
    },
    completedDate: {
      gridSize: 6,
      label: "Ngày H/T",
      type: InputType.datePicker,
      validattionYupSchema: date().nullable(),
    },
    equipment: {
      gridSize: 12,
      label: "Thiết Bị S/X",
      type: InputType.text,
      validattionYupSchema: string().max(64),
    },
    operator: {
      gridSize: 6,
      label: "Người S/X",
      type: InputType.text,
      validattionYupSchema: string().max(64),
    },
    inspector: {
      gridSize: 6,
      label: "Người Kiểm QT",
      type: InputType.text,
      validattionYupSchema: string().max(64),
    },
    amount: {
      gridSize: 6,
      label: amountLabel,
      type: InputType.number,
      validattionYupSchema: number()
        .nullable()
        .min(1)
        .test(
          "require-amount-if-entering-completed-amount",
          `Bạn cần nhập ${amountLabel} trước`,
          function(value) {
            const completedAmount = form.getControl("completedAmount")?.value as
              number | null | undefined;
            if (value == null && completedAmount != null)
              return false;
            return true;
          },
        )
        .test(
          "amount-must-not-be-less-than-completed-amount",
          completedAmountMustNotExceedAmountMessage(unitLabel),
          (value) => {
            const completedAmount = form.getControl("completedAmount")
              ?.value as number | null | undefined;
            if (value == null || completedAmount == null) return true;
            return value >= completedAmount;
          },
        ),
    },
    completedAmount: {
      gridSize: 6,
      label: `Số Lượng H/T (${unitLabel})`,
      type: InputType.number,
      validattionYupSchema: number()
        .nullable()
        .min(1)
        .test(
          "completed-amount-must-not-exceed-amount",
          completedAmountMustNotExceedAmountMessage(unitLabel),
          (value) => {
            const amount = form.getControl("amount")?.value as
              number | null | undefined;
            if (value == null || amount == null) return true;
            return value <= amount;
          },
        )
        .test(
          "completed-amount-must-match-package-amount",
          completedAmountMustMatchPackageAmountMessage,
          () => isValidCompletedAmountByPackage(form),
        ),
    },
  };
}

export const getProcessWithPrintAmountFormSchema = (
  form: IForm
): ObjectSchemaType<IUpdateWorkOrderPOProgressDto> => {
  const base = getStandardProcessFormSchema(form, 'pcs');
  return ({
    date: base.date,
    completedDate: base.completedDate,
    equipment: base.equipment,
    operator: base.operator,
    inspector: base.inspector,
    amount: base.amount,
    completedAmount: base.completedAmount,
    printPaperAmount: {
      gridSize: 6,
      label: "Số Lượng S/X (tờ)",
      type: InputType.number,
      validattionYupSchema: number()
        .nullable()
        .min(1)
        .test(
          "require-print-paper-amount-if-entering-print-paper-completed-amount",
          `Bạn cần nhập Số Lượng S/X (tờ) trước`,
          (value) => {
            const completedAmount = form.getControl("printPaperCompletedAmount")?.value as
              number | null | undefined;
            if (value == null && completedAmount != null)
              return false;
            return true;
          },
        )
        .test(
          "print-paper-amount-must-not-be-less-than-print-paper-completed-amount",
          printPaperCompletedAmountMustNotExceedAmountMessage,
          (value) => {
            const completedAmount = form.getControl("printPaperCompletedAmount")?.value as
              number | null | undefined;
            if (value == null || completedAmount == null) return true;
            return value >= completedAmount;
          },
        ),
    },
    printPaperCompletedAmount: {
      gridSize: 6,
      label: "Số Lượng H/T (tờ)",
      type: InputType.number,
      validattionYupSchema: number()
        .nullable()
        .min(1)
        .test(
          "print-paper-completed-amount-must-not-exceed-print-paper-amount",
          printPaperCompletedAmountMustNotExceedAmountMessage,
          (value) => {
            const amount = form.getControl("printPaperAmount")?.value as
              number | null | undefined;
            if (value == null || amount == null) return true;
            return value <= amount;
          },
        ),
    },
  })
}

// Mirrors WorkOrderPoAppService.UpdatePrintProgress (backend), so the
// read-only completedAmount field always reflects the current form state.
export const computePrintCompletedAmount = (
  firstGradeAmount: number | null | undefined,
  secondGradeAmount: number | null | undefined,
) => {
  if (firstGradeAmount == null && secondGradeAmount == null) return null;
  return (firstGradeAmount ?? 0) + (secondGradeAmount ?? 0);
};

// Mirrors WorkOrderPoAppService.UpdateAcceptanceProgress (backend).
export const computeAcceptanceCompletedAmounts = (
  firstGradeAmountPcs: number | null | undefined,
  secondGradeAmountPcs: number | null | undefined,
  firstGradeAmountPaper: number | null | undefined,
  secondGradeAmountPaper: number | null | undefined,
) => {
  const hasPcs = firstGradeAmountPcs != null || secondGradeAmountPcs != null;
  const hasPaper =
    firstGradeAmountPaper != null || secondGradeAmountPaper != null;

  return {
    completedAmount: hasPcs
      ? (firstGradeAmountPcs ?? 0) + (secondGradeAmountPcs ?? 0)
      : null,
    printPaperCompletedAmount: hasPaper
      ? (firstGradeAmountPaper ?? 0) + (secondGradeAmountPaper ?? 0)
      : null,
  };
};

export const getPrintProcessFormSchema = (
  form: IForm,
): ObjectSchemaType<IUpdateWorkOrderPOProgressDto> => {
  const base = getStandardProcessFormSchema(form, 'paper');
  return ({
    date: base.date,
    completedDate: base.completedDate,
    equipment: base.equipment,
    operator: base.operator,
    inspector: base.inspector,
    amount: {
      ...base.amount,
      gridSize: 12,
    },
    firstGradeAmount: {
      gridSize: 6,
      label: "S/L Hàng Loại 1",
      type: InputType.number,
      validattionYupSchema: number().nullable().min(0),
    },
    secondGradeAmount: {
      gridSize: 6,
      label: "S/L Hàng Loại 2",
      type: InputType.number,
      validattionYupSchema: number().nullable().min(0),
    },
    rejectedAmount: {
      gridSize: 6,
      label: "S/L Hư 100%",
      type: InputType.number,
      validattionYupSchema: number().nullable().min(0),
    },
    completedAmount: {
      ...base.completedAmount,
      disabled: true,
      gridSize: 6,
      helpText: "Công thức: S/L Hàng Loại 1 + S/L Hàng Loại 2",
    },
  })
}

export const getAcceptanceProcessFormSchema = (
  form: IForm,
): ObjectSchemaType<IUpdateWorkOrderPOProgressDto> => {
  const base = getProcessWithPrintAmountFormSchema(form);
  return ({
    date: base.date,
    completedDate: base.completedDate,
    equipment: base.equipment,
    operator: base.operator,
    inspector: base.inspector,
    printPaperAmount: {
      ...base.printPaperAmount,
      gridSize: 12,
    },
    firstGradeAmountPaper: {
      gridSize: 6,
      label: "S/L Hàng Loại 1 (tờ)",
      type: InputType.number,
      validattionYupSchema: number().nullable().min(0),
    },
    secondGradeAmountPaper: {
      gridSize: 6,
      label: "S/L Hàng Loại 2 (tờ)",
      type: InputType.number,
      validattionYupSchema: number().nullable().min(0),
    },
    rejectedAmountPaper: {
      gridSize: 6,
      label: "S/L Hư 100% (tờ)",
      type: InputType.number,
      validattionYupSchema: number().nullable().min(0),
    },
    printPaperCompletedAmount: {
      ...base.printPaperCompletedAmount,
      disabled: true,
      gridSize: 6,
      helpText: "Công thức: S/L Hàng Loại 1 (tờ) + S/L Hàng Loại 2 (tờ)",
    },
    amount: {
      ...base.amount,
      gridSize: 12,
    },
    firstGradeAmountPcs: {
      gridSize: 6,
      label: "S/L Hàng Loại 1 (pcs)",
      type: InputType.number,
      validattionYupSchema: number().nullable().min(0),
    },
    secondGradeAmountPcs: {
      gridSize: 6,
      label: "S/L Hàng Loại 2 (pcs)",
      type: InputType.number,
      validattionYupSchema: number().nullable().min(0),
    },
    rejectedAmountPcs: {
      gridSize: 6,
      label: "S/L Hư 100% (pcs)",
      type: InputType.number,
      validattionYupSchema: number().nullable().min(0),
    },
    completedAmount: {
      ...base.completedAmount,
      disabled: true,
      gridSize: 6,
      helpText: "Công thức: S/L Hàng Loại 1 (pcs) + S/L Hàng Loại 2 (pcs)",
    },
  })
}

export const getPackageProcessFormSchema = (
  form: IForm,
): ObjectSchemaType<IUpdateWorkOrderPOPackageProgressDto> => {
  const { completedAmount, ...base } = getStandardProcessFormSchema(form, 'pcs');
  return ({
    ...base,
    amount: {
      ...base.amount,
      gridSize: 12,
    },
    packageNumber: {
      gridSize: 12,
      label: "Số Kiện Chẵn",
      type: InputType.number,
      validattionYupSchema: number()
        .nullable()
        .test(
          "package-number-must-match-completed-amount",
          completedAmountMustMatchPackageAmountMessage,
          () => isValidCompletedAmountByPackage(form),
        ),
    },
    packageAmountPerUnit: {
      gridSize: 6,
      label: "SL/ Kiện (pcs)",
      type: InputType.number,
      validattionYupSchema: number()
        .nullable()
        .min(1)
        .test(
          "package-amount-per-unit-must-match-completed-amount",
          completedAmountMustMatchPackageAmountMessage,
          () => isValidCompletedAmountByPackage(form),
        ),
    },
    packageWeightPerUnit: {
      gridSize: 6,
      label: "TL/ Kiện (kg)",
      type: InputType.number,
      validattionYupSchema: number().nullable(),
    },
    packageAmount: {
      gridSize: 6,
      label: "S/L Kiện Lẻ 1 (pcs)",
      type: InputType.number,
      validattionYupSchema: number()
        .nullable()
        .min(1)
        .test(
          "package-amount-must-match-completed-amount",
          completedAmountMustMatchPackageAmountMessage,
          () => isValidCompletedAmountByPackage(form),
        ),
    },
    packageWeight: {
      gridSize: 6,
      label: "T/L Kiện Lẻ 1 (kg)",
      type: InputType.number,
      validattionYupSchema: number().nullable(),
    },
    packageAmount2: {
      gridSize: 6,
      label: "S/L Kiện Lẻ 2 (pcs)",
      type: InputType.number,
      validattionYupSchema: number()
        .nullable()
        .min(1)
        .test(
          "package-amount-2-must-match-completed-amount",
          completedAmountMustMatchPackageAmountMessage,
          () => isValidCompletedAmountByPackage(form),
        ),
    },
    packageWeight2: {
      gridSize: 6,
      label: "T/L Kiện Lẻ 2 (kg)",
      type: InputType.number,
      validattionYupSchema: number().nullable(),
    },
    completedAmount: {
      ...completedAmount,
      disabled: true,
      gridSize: 12,
      helpText: "Công thức: Số Kiện Chẵn x SL/ Kiện (pcs) + S/L Kiện Lẻ 1 (pcs) + S/L Kiện Lẻ 2 (pcs)",
    },
    packageNote: {
      gridSize: 12,
      label: "Ghi chú",
      type: InputType.textArea,
      validattionYupSchema: string().max(512),
    },
  })
};
